import { Avatar, Button, CloseButton, Text, Title } from "@mantine/core";
import { closeModal, openContextModal } from "@mantine/modals";
import IconTrash from "@src/assets/icons/tabler/trash.svg?react";
import type { DesktopProfile } from "@src/electron-api";
import { showErrorNotification } from "@src/utils/notifications";
import clsx from "clsx";
import { useState } from "react";
import { DEFAULT_DESKTOP_PROFILE_COLOR } from "../../constants";

let modalId: string | null = null;

export function closeDeleteProfileModal() {
	if (modalId) {
		closeModal(modalId);
		modalId = null;
	}
}

export interface DeleteProfileResult {
	success: boolean;
	error?: string;
}

export interface OpenDeleteProfileModalOptions {
	profile: DesktopProfile;
	onDelete: () => Promise<DeleteProfileResult> | DeleteProfileResult;
}

const DEFAULT_DELETE_ERROR = "Failed to delete profile. Please try again.";

/** Shared failure copy for delete-profile IPC / modal. */
export function getDeleteProfileErrorMessage(
	result: DeleteProfileResult,
): string | null {
	if (result.success) return null;
	return result.error || DEFAULT_DELETE_ERROR;
}

function DeleteProfileModalContent({
	profile,
	onClose,
	onDelete,
}: {
	profile: DesktopProfile;
	onClose: () => void;
	onDelete: () => Promise<DeleteProfileResult> | DeleteProfileResult;
}) {
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleConfirm = async () => {
		try {
			setSubmitting(true);
			setError(null);
			const result = await onDelete();
			const failureMessage = getDeleteProfileErrorMessage(result);
			if (failureMessage) {
				setError(failureMessage);
				showErrorNotification("Delete profile", failureMessage);
				setSubmitting(false);
				return;
			}
			onClose();
		} catch (err) {
			console.error("Failed to delete profile:", err);
			setError(DEFAULT_DELETE_ERROR);
			showErrorNotification("Delete profile", DEFAULT_DELETE_ERROR);
			setSubmitting(false);
		}
	};

	return (
		<div className="p-6 flex flex-col gap-4">
			<div className="flex items-start justify-between gap-3">
				<div className="flex items-center gap-3">
					<div
						className={clsx(
							"w-10 h-10 rounded-full bg-red-100 dark:bg-red-950/40",
							"flex items-center justify-center shrink-0",
						)}
					>
						<IconTrash className="w-5 h-5 text-red-600 dark:text-red-400" />
					</div>
					<div>
						<Title
							order={4}
							className="text-text-primary font-bold leading-tight"
						>
							Delete Profile
						</Title>
						<Text size="xs" className="text-text-secondary mt-0.5">
							This action cannot be undone.
						</Text>
					</div>
				</div>
				<CloseButton
					onClick={onClose}
					size="sm"
					className="-mr-1 -mt-1"
					disabled={submitting}
				/>
			</div>

			<div
				className={clsx(
					"flex items-center gap-3 p-3 rounded-lg",
					"bg-surface-subtle border border-border-subtle",
				)}
			>
				<Avatar
					src={profile.avatarUrl}
					size={36}
					radius="xl"
					style={{
						border: `2px solid ${profile.color || DEFAULT_DESKTOP_PROFILE_COLOR}`,
					}}
				>
					{profile.name[0]?.toUpperCase()}
				</Avatar>
				<div className="flex flex-col min-w-0">
					<Text size="sm" fw={600} className="text-text-primary truncate">
						{profile.name}
					</Text>
					<Text
						size="xs"
						className="text-text-tertiary truncate"
						title={profile.email}
					>
						{profile.email || "Not signed in"}
					</Text>
				</div>
			</div>

			<Text size="sm" className="text-text-secondary leading-relaxed">
				Are you sure you want to delete profile{" "}
				<span className="font-semibold text-text-primary">
					&ldquo;{profile.name}&rdquo;
				</span>
				? This will clear its saved login and session data.
			</Text>

			{error && (
				<Text size="sm" className="text-danger">
					{error}
				</Text>
			)}

			<div className="flex items-center justify-end gap-3 pt-1">
				<Button
					variant="subtle"
					color="gray"
					onClick={onClose}
					disabled={submitting}
				>
					Cancel
				</Button>
				<Button
					color="red"
					variant="filled"
					onClick={handleConfirm}
					loading={submitting}
				>
					Delete Profile
				</Button>
			</div>
		</div>
	);
}

export function openDeleteProfileModal({
	profile,
	onDelete,
}: OpenDeleteProfileModalOptions) {
	closeDeleteProfileModal();
	modalId = openContextModal({
		modal: "buttonsModal",
		centered: true,
		withCloseButton: false,
		padding: 0,
		radius: "md",
		size: "420px",
		zIndex: 9999,
		onClose: () => {
			modalId = null;
		},
		innerProps: {
			children: (
				<DeleteProfileModalContent
					profile={profile}
					onClose={closeDeleteProfileModal}
					onDelete={onDelete}
				/>
			),
		},
	});
}
